import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, ShieldAlert, Key, Unlock, Lock, Copy } from 'lucide-react';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { hashPasscode, generateRecoveryCodes } from '../../utils/security';
import { usePopup } from '../../context/PopupContext';

export const ShadowsLockPage = () => {
    const navigate = useNavigate();
    const { showConfirm } = usePopup();
    const currentUser = auth.currentUser;

    // Modes: LOADING, SETUP_1, SETUP_2, UNLOCK, RECOVERY, CODES_DISPLAY, SETTINGS
    const [mode, setMode] = useState('LOADING');
    const [pin, setPin] = useState('');
    const [confirmPin, setConfirmPin] = useState('');
    const [error, setError] = useState('');
    const [recoveryCodeInput, setRecoveryCodeInput] = useState('');
    const [generatedCodes, setGeneratedCodes] = useState([]);
    const [userData, setUserData] = useState(null);

    useEffect(() => {
        const fetchUserData = async () => {
            if (!currentUser) return;
            try {
                const docSnap = await getDoc(doc(db, 'users', currentUser.uid, 'settings', 'privacy'));
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setUserData(data);
                    if (data.shadowPasscode) {
                        setMode('UNLOCK');
                    } else {
                        setMode('SETUP_1');
                    }
                } else {
                    setMode('SETUP_1');
                }
            } catch (err) {
                console.error("Failed to load security settings:", err);
                toast.error("Error: Could not verify security status. Please check connection.");
                navigate(-1);
            }
        };
        fetchUserData();
    }, [currentUser, navigate]);

    useEffect(() => {
        if (pin.length === 4 && (mode === 'UNLOCK' || mode === 'SETUP_1' || mode === 'SETUP_2')) {
            processPin(pin);
        }
    }, [pin]);

    const processPin = async (inputPin) => {
        if (mode === 'UNLOCK') {
            const hash = hashPasscode(inputPin);
            if (hash === userData.shadowPasscode) {
                setPin('');
                setError('');
                setMode('SETTINGS'); // Logged into management mode
            } else {
                setError('Incorrect Passcode');
                setPin('');
            }
        } 
        else if (mode === 'SETUP_1') {
            setConfirmPin(inputPin);
            setPin('');
            setMode('SETUP_2');
        } 
        else if (mode === 'SETUP_2') {
            if (inputPin === confirmPin) {
                const codes = generateRecoveryCodes();
                setGeneratedCodes(codes);
                setMode('CODES_DISPLAY');
            } else {
                setError("Passcodes didn't match. Try again.");
                setPin('');
                setMode('SETUP_1');
            }
        }
    };

    const handleRecoverySubmit = async () => {
        const code = recoveryCodeInput.trim().toUpperCase();
        if (code.length !== 8) {
            setError('Invalid Code Length');
            return;
        }

        const storedCodes = userData?.shadowRecoveryCodes || [];
        const matchIndex = storedCodes.findIndex(c => c.code === code && !c.used);

        if (matchIndex !== -1) {
            const newCodes = [...storedCodes];
            newCodes[matchIndex].used = true;
            
            try {
                await updateDoc(doc(db, 'users', currentUser.uid, 'settings', 'privacy'), { 
                    shadowRecoveryCodes: newCodes 
                });
                setUserData({ ...userData, shadowRecoveryCodes: newCodes }); 
                setMode('SETUP_1'); // Allow them to reset it
                setPin('');
                setRecoveryCodeInput('');
                toast.error("Passcode reset! You can now create a new one.");
            } catch (e) {
                setError('Failed to update codes');
            }
        } else {
            setError('Invalid or Already Used Code');
        }
    };

    const savePasscode = async () => {
        const hash = hashPasscode(confirmPin);
        try {
            await setDoc(doc(db, 'users', currentUser.uid, 'settings', 'privacy'), {
                shadowPasscode: hash,
                shadowRecoveryCodes: generatedCodes,
                updatedAt: new Date()
            }, { merge: true });
            toast.error("Passcode enabled securely!");
            navigate(-1);
        } catch (e) {
            toast.error("Failed to save passcode. Please try again.");
        }
    };

    const disablePasscode = async () => {
        showConfirm({
            title: "Disable Passcode",
            message: "Are you sure you want to disable your Shadows Passcode? Your shadows will no longer be protected.",
            isDestructive: true,
            confirmText: "Disable",
            onConfirm: async () => {
                try {
                    await updateDoc(doc(db, 'users', currentUser.uid, 'settings', 'privacy'), {
                        shadowPasscode: null,
                        shadowRecoveryCodes: null
                    });
                    toast.success("Passcode disabled.");
                    navigate(-1);
                } catch (e) {
                    toast.error("Failed to disable passcode.");
                }
            }
        });
    };

    const renderNumpad = () => {
        const isRecovery = mode === 'RECOVERY';
        const value = isRecovery ? recoveryCodeInput : pin;
        const onChange = isRecovery ? setRecoveryCodeInput : setPin;
        const maxLen = isRecovery ? 8 : 4;

        return (
            <div className="w-full max-w-[280px] mx-auto mt-8">
                {isRecovery && (
                    <div className="mb-6 flex justify-center">
                        <input
                            type="text"
                            value={recoveryCodeInput}
                            onChange={(e) => setRecoveryCodeInput(e.target.value.toUpperCase().slice(0, 8))}
                            className="bg-[#111] border border-[#333] rounded-lg text-white text-center text-xl tracking-[0.3em] font-mono py-3 w-full outline-none focus:border-[#0BD9E5] transition-colors"
                            placeholder="XXXXXXXX"
                        />
                    </div>
                )}
                {!isRecovery && (
                    <div className="flex justify-center gap-4 mb-8">
                        {[0, 1, 2, 3].map((i) => (
                            <div key={i} className={`w-4 h-4 rounded-full ${i < pin.length ? 'bg-[#0BD9E5] shadow-[0_0_10px_#0BD9E5]' : 'bg-[#333]'}`} />
                        ))}
                    </div>
                )}
                
                <div className="grid grid-cols-3 gap-4">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                        <button
                            key={num}
                            onClick={() => {
                                if (value.length < maxLen) {
                                    onChange(prev => prev + num);
                                    setError('');
                                }
                            }}
                            className="w-16 h-16 rounded-full bg-[#111] border border-[#222] text-white text-2xl font-light hover:bg-[#222] transition-colors mx-auto flex items-center justify-center"
                        >
                            {num}
                        </button>
                    ))}
                    <div />
                    <button
                        onClick={() => {
                            if (value.length < maxLen) {
                                onChange(prev => prev + '0');
                                setError('');
                            }
                        }}
                        className="w-16 h-16 rounded-full bg-[#111] border border-[#222] text-white text-2xl font-light hover:bg-[#222] transition-colors mx-auto flex items-center justify-center"
                    >
                        0
                    </button>
                    <button
                        onClick={() => {
                            onChange(prev => prev.slice(0, -1));
                            setError('');
                        }}
                        className="w-16 h-16 rounded-full bg-transparent text-[#666] hover:text-white transition-colors mx-auto flex items-center justify-center"
                    >
                        ⌫
                    </button>
                </div>

                {isRecovery && (
                    <button 
                        onClick={handleRecoverySubmit}
                        className="w-full mt-6 bg-[#0BD9E5] text-black font-bold py-3 rounded-full hover:bg-[#0BD9E5]/80 transition-colors"
                    >
                        Verify Recovery Code
                    </button>
                )}
            </div>
        );
    };

    if (mode === 'LOADING') {
        return (
            <div className="flex-1 flex items-center justify-center min-h-screen bg-[#0a0a0a]">
                <div className="w-8 h-8 rounded-full border-t-2 border-[#0BD9E5] animate-spin" />
            </div>
        );
    }

    return (
        <main className="flex-1 w-full flex flex-col bg-transparent">
            {/* Header */}
            <div className="sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-md z-40 border-b border-[#1a1a1a] flex items-center h-[60px] px-4">
                <h1 className="text-xl font-bold text-white pl-2">Shadows Lock Management</h1>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center p-6">
                
                {mode === 'SETUP_1' && (
                    <div className="text-center w-full max-w-md">
                        <div className="w-20 h-20 bg-[#0BD9E5]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Lock size={32} className="text-[#0BD9E5]" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Create Passcode</h2>
                        <p className="text-gray-400 mb-8">Enter a 4-digit PIN to secure your Shadows</p>
                        {error ? <p className="text-red-500 mb-4">{error}</p> : null}
                        {renderNumpad()}
                    </div>
                )}

                {mode === 'SETUP_2' && (
                    <div className="text-center w-full max-w-md">
                        <div className="w-20 h-20 bg-[#0BD9E5]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <CheckCircle2 size={32} className="text-[#0BD9E5]" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Confirm Passcode</h2>
                        <p className="text-gray-400 mb-8">Re-enter your PIN to verify</p>
                        {error ? <p className="text-red-500 mb-4">{error}</p> : null}
                        {renderNumpad()}
                    </div>
                )}

                {mode === 'UNLOCK' && (
                    <div className="text-center w-full max-w-md">
                        <div className="w-20 h-20 bg-[#111] rounded-full flex items-center justify-center mx-auto mb-6 border border-[#333]">
                            <Key size={32} className="text-white" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Enter Passcode</h2>
                        <p className="text-gray-400 mb-8">Unlock Shadow Management</p>
                        {error ? <p className="text-red-500 mb-4">{error}</p> : null}
                        {renderNumpad()}
                        
                        <button 
                            onClick={() => { setMode('RECOVERY'); setError(''); setPin(''); }}
                            className="mt-8 text-[#888] hover:text-white transition-colors text-sm"
                        >
                            Forgot Passcode?
                        </button>
                    </div>
                )}

                {mode === 'RECOVERY' && (
                    <div className="text-center w-full max-w-md">
                        <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <ShieldAlert size={32} className="text-red-500" />
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-2">Account Recovery</h2>
                        <p className="text-gray-400 mb-8">Enter one of your 8-character recovery codes</p>
                        {error ? <p className="text-red-500 mb-4">{error}</p> : null}
                        {renderNumpad()}
                        
                        <button 
                            onClick={() => { setMode('UNLOCK'); setError(''); setRecoveryCodeInput(''); }}
                            className="mt-8 text-[#888] hover:text-white transition-colors text-sm"
                        >
                            Cancel Recovery
                        </button>
                    </div>
                )}

                {mode === 'CODES_DISPLAY' && (
                    <div className="w-full max-w-md">
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <ShieldCheck size={32} className="text-green-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2">Recovery Codes</h2>
                            <p className="text-gray-400">Save these codes in a secure place. You can use them to unlock your shadows if you forget your PIN.</p>
                        </div>
                        
                        <div className="bg-[#111] border border-[#333] rounded-xl p-4 mb-8">
                            <div className="grid grid-cols-2 gap-4">
                                {generatedCodes.map((c, i) => (
                                    <div key={i} className="bg-[#1a1a1a] rounded p-2 text-center text-white font-mono tracking-wider">
                                        {c.code}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="flex flex-col gap-3">
                            <button 
                                onClick={() => {
                                    navigator.clipboard.writeText(generatedCodes.map(c => c.code).join('\n'));
                                    toast.success('Codes copied to clipboard!');
                                }}
                                className="flex items-center justify-center gap-2 bg-[#222] text-white py-3 rounded-full hover:bg-[#333] transition-colors"
                            >
                                <Copy size={18} /> Copy Codes
                            </button>
                            <button 
                                onClick={savePasscode}
                                className="bg-[#0BD9E5] text-black font-bold py-3 rounded-full hover:bg-[#0BD9E5]/80 transition-colors"
                            >
                                I've saved them securely
                            </button>
                        </div>
                    </div>
                )}

                {mode === 'SETTINGS' && (
                    <div className="w-full max-w-md">
                        <div className="text-center mb-8">
                            <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-6">
                                <Unlock size={32} className="text-green-500" />
                            </div>
                            <h2 className="text-2xl font-bold text-white mb-2">Shadow Settings Unlocked</h2>
                            <p className="text-gray-400">Manage your shadow security</p>
                        </div>
                        
                        <div className="bg-[#111] border border-[#222] rounded-xl overflow-hidden">
                            <button 
                                onClick={() => setMode('SETUP_1')}
                                className="w-full p-4 flex items-center justify-between hover:bg-[#1a1a1a] transition-colors border-b border-[#222]"
                            >
                                <span className="text-white">Change Passcode</span>
                                <ArrowLeft size={16} className="text-gray-500 rotate-180" />
                            </button>
                            <button 
                                onClick={disablePasscode}
                                className="w-full p-4 flex items-center justify-between hover:bg-red-500/10 transition-colors"
                            >
                                <span className="text-red-500">Disable Passcode</span>
                                <ArrowLeft size={16} className="text-red-500/50 rotate-180" />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </main>
    );
};

export default ShadowsLockPage;
